import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Project, TablesInsert, TablesUpdate } from '@t90/common/supabase';
import { useSupabase, queryKeys } from '../provider';

// =====================================================
// PROJECT QUERIES
// =====================================================

interface ProjectFilters {
  orderId?: string;
  status?: string;
  locationId?: string;
  serviceItemId?: string;
  limit?: number;
}

export function useProjects(filters?: ProjectFilters) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: queryKeys.projects.list(filters),
    queryFn: async () => {
      let query = supabase
        .from('projects')
        .select(`
          *,
          order:orders(id, order_number, customer:customers(id, customer_name)),
          service_item:service_items(*)
        `)
        .order('created_at', { ascending: false });

      if (filters?.orderId && filters.orderId.trim() !== '') {
        query = query.eq('order_id', filters.orderId);
      }
      if (filters?.status && filters.status.trim() !== '') {
        query = query.eq('project_status', filters.status);
      }
      if (filters?.locationId && filters.locationId.trim() !== '') {
        query = query.eq('location_id', filters.locationId);
      }
      if (filters?.serviceItemId && filters.serviceItemId.trim() !== '') {
        query = query.eq('service_item_id', filters.serviceItemId);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });
}

export function useProject(projectId: string | undefined) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: queryKeys.projects.detail(projectId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          order:orders(id, order_number, order_status, due_date, customer:customers(id, customer_name)),
          service_item:service_items(id, service_item_name, service_type:service_types(id, service_type_name)),
          location:locations(id, location_name, location_code)
        `)
        .eq('id', projectId!)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!projectId && projectId !== 'new',
  });
}

// =====================================================
// PROJECT MUTATIONS
// =====================================================

type ProjectInsert = TablesInsert<'projects'>;
type ProjectUpdate = TablesUpdate<'projects'>;

export function useCreateProject() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (project: ProjectInsert) => {
      const { data, error } = await supabase
        .from('projects')
        .insert(project as never)
        .select()
        .single();

      if (error) throw error;
      return data as Project;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(data.order_id) });
      toast.success(`Project ${data.project_number} created successfully`);
    },
    onError: (error) => {
      toast.error(`Failed to create project: ${error.message}`);
    },
  });
}

export function useUpdateProject() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: ProjectUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('projects')
        .update(updates as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Project;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.id) });
      toast.success(`Project ${data.project_number} updated successfully`);
    },
    onError: (error) => {
      toast.error(`Failed to update project: ${error.message}`);
    },
  });
}

export function useUpdateProjectStatus() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: Project['project_status'];
    }) => {
      const { data, error } = await supabase
        .from('projects')
        .update({ project_status: status } as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Project;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.id) });
      toast.success(`Project status updated to ${data.project_status}`);
    },
    onError: (error) => {
      toast.error(`Failed to update project status: ${error.message}`);
    },
  });
}

export function useUpdateProjectProgress() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      completedQuantity,
    }: {
      id: string;
      completedQuantity: number;
    }) => {
      const { data, error } = await supabase
        .from('projects')
        .update({ completed_quantity: completedQuantity } as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Project;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.id) });
    },
    onError: (error) => {
      toast.error(`Failed to update project progress: ${error.message}`);
    },
  });
}
